import { useEffect, useMemo, useState } from 'react'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'
import { getPeriodoMesAtual } from '../../../../services/util/date.service'
import { errorHandler } from '../../../../services/util/error.service'
import { gerarArquivoExcel } from '../../../../services/util/excel.service'
import EstoqueHttp from '../../../../services/api/https/estoque/Estoque.http'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCH_INITIAL_INFO: 'Adquirindo empresas',
  GET_PICKINGS: 'Adquirindo pickings',
  GET_REPORT: 'Gerando relatório',
}

const INITIAL_STATE = {
  companies: [],
  companySelected: null,
  pInicial: getPeriodoMesAtual().dataInicial,
  pFinal: getPeriodoMesAtual().dataFinal,
  pickings: [],
  pickingOptions: [],
  selectedPicking: null,
  rows: [],
  filteredRows: [],
  globalFilter: '',
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
  successMessage: null,
}

const GLOBAL_FILTER_FIELDS = [
  'product_code',
  'produto',
  'qtd_picking',
  'qtd_internada',
  'diferenca',
  'posicoes_estoque',
]

function toDate(value) {
  if (value instanceof Date) return value
  if (typeof value === 'string') return new Date(value)
  return null
}

function isValidDate(value) {
  return value instanceof Date && !isNaN(value.getTime())
}

function filterRows(rows, globalFilter) {
  const filter = String(globalFilter || '')
    .trim()
    .toLowerCase()

  if (!filter) return rows

  return rows.filter(row =>
    GLOBAL_FILTER_FIELDS.some(field =>
      String(row[field] || '')
        .toLowerCase()
        .includes(filter),
    ),
  )
}

function formatNumber(value) {
  const numberValue = Number(value || 0)
  if (Number.isInteger(numberValue)) return numberValue
  return Number(numberValue.toFixed(4))
}

export default function useComparativoEntradaAvulsoPicking() {
  const [state, setState] = useState({ ...INITIAL_STATE })

  useEffect(() => {
    setState(prev => ({
      ...prev,
      filteredRows: filterRows(prev.rows, prev.globalFilter),
    }))
  }, [state.rows, state.globalFilter])

  const localDispatch = useMemo(() => {
    return {
      resetState() {
        setState(prev => ({
          ...prev,
          errorMessage: null,
          successMessage: null,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      onInit() {
        setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.FETCH_INITIAL_INFO }))
      },

      getCompaniesAssociatedToUser() {
        try {
          const empresas = getEmpresas()
          setState(prev => ({
            ...prev,
            companies: empresas,
            companySelected: empresas && empresas.length === 1 ? empresas[0] : prev.companySelected,
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (e) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(e),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onCompanyChange(companySelected) {
        setState(prev => ({
          ...prev,
          companySelected,
          pickings: [],
          pickingOptions: [],
          selectedPicking: null,
          rows: [],
          filteredRows: [],
          globalFilter: '',
        }))
      },

      onPeriodoInicialChange(value) {
        const pInicial = toDate(value)
        if (!isValidDate(pInicial)) return

        setState(prev => ({
          ...prev,
          pInicial,
          pickings: [],
          pickingOptions: [],
          selectedPicking: null,
          rows: [],
          filteredRows: [],
          globalFilter: '',
        }))
      },

      onPeriodoFinalChange(value) {
        const pFinal = toDate(value)
        if (!isValidDate(pFinal)) return

        setState(prev => ({
          ...prev,
          pFinal,
          pickings: [],
          pickingOptions: [],
          selectedPicking: null,
          rows: [],
          filteredRows: [],
          globalFilter: '',
        }))
      },

      onBtnPesquisar(companySelected, tpId, pInicial, pFinal) {
        if (!companySelected) {
          setState(prev => ({ ...prev, errorMessage: 'Selecione uma empresa' }))
          return
        }

        if (!tpId || tpId === 0) {
          setState(prev => ({ ...prev, errorMessage: 'Selecione um TP para buscar' }))
          return
        }

        if (!isValidDate(pInicial)) {
          setState(prev => ({ ...prev, errorMessage: 'Período inicial inválido' }))
          return
        }

        if (!isValidDate(pFinal)) {
          setState(prev => ({ ...prev, errorMessage: 'Período final inválido' }))
          return
        }

        setState(prev => ({
          ...prev,
          loadingMessage: LOAD_MESSAGE.GET_PICKINGS,
          pickings: [],
          pickingOptions: [],
          selectedPicking: null,
          rows: [],
          filteredRows: [],
          globalFilter: '',
        }))
      },

      async getPickingsPorEmpresa(companyId, tpId, pInicial, pFinal) {
        try {
          const data = await EstoqueHttp.getPickingsComparativoEntradaAvulso(
            companyId,
            tpId,
            pInicial,
            pFinal,
          )

          const pickings = Array.isArray(data) ? data : []

          if (pickings.length === 0) {
            setState(prev => ({
              ...prev,
              pickings: [],
              pickingOptions: [],
              selectedPicking: null,
              rows: [],
              filteredRows: [],
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: 'Nenhum picking encontrado no período selecionado',
            }))
            return
          }

          const pickingOptions = pickings.map(picking => ({
            label: `${picking.id} - ${picking.name} (${picking.status})`,
            value: picking.id,
          }))

          setState(prev => ({
            ...prev,
            pickings,
            pickingOptions,
            selectedPicking: null,
            rows: [],
            filteredRows: [],
            globalFilter: '',
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (e) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(e),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onPickingChange(selectedPicking) {
        setState(prev => ({
          ...prev,
          selectedPicking,
          rows: [],
          filteredRows: [],
          globalFilter: '',
        }))
      },

      onGerarRelatorio(tpId, selectedPicking) {
        if (!selectedPicking) {
          setState(prev => ({ ...prev, errorMessage: 'Selecione um picking' }))
          return
        }

        if (!tpId || tpId === 0) {
          setState(prev => ({ ...prev, errorMessage: 'Selecione um TP para buscar' }))
          return
        }

        setState(prev => ({
          ...prev,
          loadingMessage: LOAD_MESSAGE.GET_REPORT,
          rows: [],
          filteredRows: [],
          globalFilter: '',
        }))
      },

      async getRelatorio(tpId, pickingId) {
        try {
          const rows = await EstoqueHttp.getComparativoEntradaAvulsoPicking(tpId, pickingId)

          setState(prev => ({
            ...prev,
            rows: rows || [],
            filteredRows: rows || [],
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        } catch (e) {
          setState(prev => ({
            ...prev,
            errorMessage: errorHandler(e),
            loadingMessage: LOAD_MESSAGE.NONE,
          }))
        }
      },

      onGlobalFilterChange(globalFilter) {
        setState(prev => ({ ...prev, globalFilter }))
      },

      exportExcel(rows) {
        if (!rows || rows.length === 0) return

        const data = [
          ['Código', 'Produto', 'Qtd Picking', 'Qtd Internada', 'Diferença', 'Posições de Estoque'],
        ]

        rows.forEach(row => {
          data.push([
            row.product_code || '',
            row.produto || '',
            formatNumber(row.qtd_picking),
            formatNumber(row.qtd_internada),
            formatNumber(row.diferenca),
            row.posicoes_estoque || '',
          ])
        })

        gerarArquivoExcel('Comparativo', data, 'Comparativo_Entrada_Avulso_Picking.xlsx')
      },
    }
  }, [])

  return { state, localDispatch }
}
